import React, { useEffect, useMemo } from 'react';
import { Alert, Button, Form as AntdForm } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../basic';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import { CollapseBlock, MainTitle, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import checkPermission from '../../../../../utils/helpers/checkPermission.jsx';
import {
  useGetPhdDissertationDefenseApplicationsQuery,
  useGetPhdDissertationExamApplicationsQuery,
  useGetPhdSubjectsExamApplicationsQuery,
  useSavePhdDissertationDefenseApplicationMutation,
  useSavePhdDissertationExamApplicationMutation,
  useSavePhdSubjectsExamApplicationMutation,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import usePhdStudentProfile, {
  mapStudentProfileToFormDefaults,
} from '../usePhdStudentProfile.jsx';
import PhdDissertationExamApplicationsEditForm from '../PhdDissertationExamApplications/PhdDissertationExamApplicationsEditForm.jsx';
import PhdDissertationDefenseApplicationsEditForm from '../PhdDissertationDefenseApplications/PhdDissertationDefenseApplicationsEditForm.jsx';
import PhdSubjectsExamApplicationsEditForm from '../PhdSubjectsExamApplications/PhdSubjectsExamApplicationsEditForm.jsx';
import {
  getApplicationSubmissionState,
  isApplicationSubmitted,
  PHD_APPLICATION_CHANGE_CONTACT_MESSAGE,
} from '../utils/applicationSubmissionState.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';

const TYPE_VALUES = ['dissertationExam', 'dissertationDefense', 'subjectsExam'];

const toComparableValue = (value) => String(value ?? '').trim();

const getStudentOwnedApplications = (applications, studentId) => {
  if (!Array.isArray(applications)) {
    return [];
  }

  const comparableStudentId = toComparableValue(studentId);
  if (!comparableStudentId) {
    return applications;
  }

  const filtered = applications.filter((application) => {
    const ownerId =
      application?.phd_student_id ??
      application?.student?.id ??
      application?.student_id;
    return toComparableValue(ownerId) === comparableStudentId;
  });

  return filtered.length > 0 ? filtered : applications;
};

const baseInitialValuesByType = {
  dissertationExam: {
    phd_student_id: undefined,
    phd_school_year_id: undefined,
    thesis_title_sk: undefined,
    thesis_title_en: undefined,
    student_display_name: undefined,
    supervisor_display_name: undefined,
    student_study_form: undefined,
    student_study_field: undefined,
    student_study_program: undefined,
  },
  dissertationDefense: {
    phd_student_id: undefined,
    phd_school_year_id: undefined,
    student_display_name: undefined,
    supervisor_display_name: undefined,
    student_achieved_higher_education: undefined,
    student_full_address: undefined,
    student_study_form: undefined,
    student_study_field: undefined,
    student_study_program: undefined,
    student_nationality: undefined,
    student_ethnicity: undefined,
  },
  subjectsExam: {
    phd_student_id: undefined,
    phd_school_year_id: undefined,
    student_display_name: undefined,
    supervisor_display_name: undefined,
    student_study_form: undefined,
    student_study_field: undefined,
    student_study_program: undefined,
  },
};

const PhdApplicationsCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const { saveSuccess, saveFailed } = useToastMessages();

  const canLoadStudentProfile = Boolean(
    user?.permissions?.['phd-student-plan.index'],
  );
  const { data: studentProfile, isFetching: isStudentLoading } =
    usePhdStudentProfile({
      enabled: canLoadStudentProfile,
    });
  const isStudentContext = Boolean(studentProfile?.id) && !isAdmin;

  const canViewDissertationExam = checkPermission(
    user,
    'phd-dissertation-exam-applications.index',
    isAdmin,
  );
  const canViewDissertationDefense = checkPermission(
    user,
    'phd-dissertation-defense-applications.index',
    isAdmin,
  );
  const canViewSubjectsExam = checkPermission(
    user,
    'phd-subjects-exam-applications.index',
    isAdmin,
  );

  const canCreateDissertationExam = checkPermission(
    user,
    'phd-dissertation-exam-applications.store',
    isAdmin,
  );
  const canCreateDissertationDefense = checkPermission(
    user,
    'phd-dissertation-defense-applications.store',
    isAdmin,
  );
  const canCreateSubjectsExam = checkPermission(
    user,
    'phd-subjects-exam-applications.store',
    isAdmin,
  );

  const {
    data: dissertationExamApplications,
    isLoading: isLoadingDissertationExam,
  } = useGetPhdDissertationExamApplicationsQuery(undefined, {
    skip: !canViewDissertationExam,
  });
  const {
    data: dissertationDefenseApplications,
    isLoading: isLoadingDissertationDefense,
  } = useGetPhdDissertationDefenseApplicationsQuery(undefined, {
    skip: !canViewDissertationDefense,
  });
  const { data: subjectsExamApplications, isLoading: isLoadingSubjectsExam } =
    useGetPhdSubjectsExamApplicationsQuery(undefined, {
      skip: !canViewSubjectsExam,
    });

  const [
    saveDissertationExamApplication,
    { isLoading: isSavingDissertationExam },
  ] = useSavePhdDissertationExamApplicationMutation();
  const [
    saveDissertationDefenseApplication,
    { isLoading: isSavingDissertationDefense },
  ] = useSavePhdDissertationDefenseApplicationMutation();
  const [saveSubjectsExamApplication, { isLoading: isSavingSubjectsExam }] =
    useSavePhdSubjectsExamApplicationMutation();

  const {
    studentOptions,
    students,
    schoolYearOptions,
    studyFieldLabelOptions,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({ includeStudents: !isStudentContext });

  const typeDefinitions = useMemo(() => {
    const getExistingStudentApplication = (applications) => {
      if (!isStudentContext) {
        return null;
      }

      const ownedApplications = getStudentOwnedApplications(
        applications,
        studentProfile?.id,
      );
      return ownedApplications[0] ?? null;
    };

    return [
      {
        value: 'dissertationExam',
        label: t('Prihláška na dizertačnú skúšku'),
        formTitle: t('Pridanie prihlášky na dizertačnú skúšku'),
        canCreate: canCreateDissertationExam,
        existingApplication: getExistingStudentApplication(
          dissertationExamApplications,
        ),
        formComponent: PhdDissertationExamApplicationsEditForm,
        isSaving: isSavingDissertationExam,
        submit: (values) =>
          saveDissertationExamApplication({ body: values }).unwrap(),
      },
      {
        value: 'dissertationDefense',
        label: t('Prihláška na obhajobu dizertačnej práce'),
        formTitle: t('Pridanie prihlášky na obhajobu'),
        canCreate: canCreateDissertationDefense,
        existingApplication: getExistingStudentApplication(
          dissertationDefenseApplications,
        ),
        formComponent: PhdDissertationDefenseApplicationsEditForm,
        isSaving: isSavingDissertationDefense,
        submit: (values) =>
          saveDissertationDefenseApplication({ body: values }).unwrap(),
      },
      {
        value: 'subjectsExam',
        label: t('Prihláška na štátnu skúšku z povinných predmetov'),
        formTitle: t('Pridanie prihlášky na skúšku z povinných predmetov'),
        canCreate: canCreateSubjectsExam,
        existingApplication: getExistingStudentApplication(
          subjectsExamApplications,
        ),
        formComponent: PhdSubjectsExamApplicationsEditForm,
        isSaving: isSavingSubjectsExam,
        submit: (values) =>
          saveSubjectsExamApplication({ body: values }).unwrap(),
      },
    ].filter((type) => type.canCreate);
  }, [
    canCreateDissertationDefense,
    canCreateDissertationExam,
    canCreateSubjectsExam,
    dissertationDefenseApplications,
    dissertationExamApplications,
    isSavingDissertationDefense,
    isSavingDissertationExam,
    isSavingSubjectsExam,
    isStudentContext,
    saveDissertationDefenseApplication,
    saveDissertationExamApplication,
    saveSubjectsExamApplication,
    studentProfile?.id,
    subjectsExamApplications,
    t,
  ]);

  const searchType = searchParams.get('type');
  const dashboardPrefill = useMemo(() => {
    const studentId = searchParams.get('phd_student_id');

    return {
      phd_student_id: /^\d+$/.test(studentId || '')
        ? Number(studentId)
        : undefined,
    };
  }, [searchParams]);
  const selectedTypeCandidate = searchType || TYPE_VALUES[0];

  const activeType = useMemo(() => {
    if (typeDefinitions.some((type) => type.value === selectedTypeCandidate)) {
      return selectedTypeCandidate;
    }
    return typeDefinitions[0]?.value || null;
  }, [selectedTypeCandidate, typeDefinitions]);

  const activeConfig =
    typeDefinitions.find((type) => type.value === activeType) ??
    typeDefinitions[0];

  const initialValues = useMemo(() => {
    if (!activeConfig) {
      return null;
    }

    const baseValues = baseInitialValuesByType[activeConfig.value] ?? {};
    if (!isStudentContext) {
      return { ...baseValues, ...dashboardPrefill };
    }

    return {
      ...baseValues,
      ...dashboardPrefill,
      ...mapStudentProfileToFormDefaults(studentProfile),
    };
  }, [activeConfig, dashboardPrefill, isStudentContext, studentProfile]);
  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initialValues);

  const isLoadingApplications =
    isLoadingDissertationExam ||
    isLoadingDissertationDefense ||
    isLoadingSubjectsExam;

  const isLoading =
    isReferenceLoading || isStudentLoading || isLoadingApplications;
  const pageTitle = t('Nová prihláška');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const openExistingApplication = () => {
    if (!activeConfig?.existingApplication?.id) {
      return;
    }

    const editRouteByType = {
      dissertationExam: routes.cmsPhdDissertationExamApplicationsEdit,
      dissertationDefense: routes.cmsPhdDissertationDefenseApplicationsEdit,
      subjectsExam: routes.cmsPhdSubjectsExamApplicationsEdit,
    };

    const editRoute = editRouteByType[activeConfig.value];
    if (!editRoute) {
      return;
    }

    navigate(`${editRoute}/${activeConfig.existingApplication.id}`);
  };

  const formStudents = useMemo(() => {
    if (isStudentContext && studentProfile) {
      return [studentProfile];
    }

    return students;
  }, [isStudentContext, studentProfile, students]);

  const handleSubmit = async (values) => {
    if (!activeConfig) {
      return;
    }
    try {
      await activeConfig.submit(values);
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdApplications));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if (!typeDefinitions.length) {
    return (
      <>
        <AppBarCMS
          title={pageTitle}
          backUrl={routes.cmsPhdApplications}
          backUrlTooltip={t('Prihlášky')}
        />
        <div style={{ margin: '10px' }}>
          <PhdPageContext
            title={t('Ako funguje vytvorenie prihlášky?')}
            description={t(
              'Dostupné typy prihlášok závisia od vašej roly a údajov o štúdiu. Po výbere typu systém zobrazí príslušný formulár a pri uložení overí podmienky vyplývajúce zo stavu štúdia.',
            )}
          />
          <CollapseBlock>
            <MainTitle title={pageTitle} />
            <p>{t('Nemáte oprávnenie na vytvorenie prihlášky.')}</p>
          </CollapseBlock>
        </div>
      </>
    );
  }

  if (isLoading || !activeConfig || !initialValues) {
    return <Spinner />;
  }

  const ActiveFormComponent = activeConfig.formComponent;

  return (
    <>
      {!activeConfig?.existingApplication && modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdApplications}
        backUrlTooltip={t('Prihlášky')}
        permissionsBasic="phd-applications"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje vytvorenie prihlášky?')}
          description={t(
            'Vyberte typ prihlášky a doktoranda, potom doplňte požadované údaje. Ak prihláška rovnakého typu už existuje, otvorte ju namiesto vytvorenia ďalšej.',
          )}
        />
        <CollapseBlock>
          <MainTitle title={activeConfig.formTitle} />
          <div style={{ marginBottom: 16 }}>
            <PhdHelpHint
              label={t('Čo znamená typ prihlášky?')}
              title={t(
                'Typ určuje povinné podklady aj nasledujúci krok: skúšobnú komisiu, skúšky z predmetov alebo obhajobnú komisiu.',
              )}
            />
          </div>
          {activeConfig.existingApplication ? (
            <div>
              <p>
                {isApplicationSubmitted(activeConfig.existingApplication)
                  ? t(
                      'Prihláška tohto typu už bola odovzdaná. Môžete ju už len zobraziť.',
                    )
                  : t(
                      'Prihláška tohto typu už existuje. Môžete ju iba upraviť.',
                    )}
              </p>
              <p>
                {t('Aktuálny stav')}:{' '}
                {
                  getApplicationSubmissionState(
                    activeConfig.existingApplication,
                    t,
                  ).label
                }
              </p>
              {isApplicationSubmitted(activeConfig.existingApplication) ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={t(PHD_APPLICATION_CHANGE_CONTACT_MESSAGE)}
                />
              ) : null}
              <Button
                htmlType="button"
                type="primary"
                onClick={openExistingApplication}
              >
                {isApplicationSubmitted(activeConfig.existingApplication)
                  ? t('Zobraziť existujúcu prihlášku')
                  : t('Otvoriť existujúcu prihlášku')}
              </Button>
            </div>
          ) : (
            <Form
              form={form}
              initialValues={initialValues}
              onFinish={handleSubmit}
              onValuesChange={onValuesChange}
            >
              <ActiveFormComponent
                studentOptions={studentOptions}
                students={formStudents}
                schoolYearOptions={schoolYearOptions}
                ensureSchoolYearSelection={ensureSchoolYearSelection}
                isCreatingSchoolYear={isCreatingSchoolYear}
                studyFieldLabelOptions={studyFieldLabelOptions}
                isSubmitting={activeConfig.isSaving}
                isStudentContext={isStudentContext}
              />
            </Form>
          )}
        </CollapseBlock>
      </div>
    </>
  );
};

export default PhdApplicationsCreatePage;
